import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Command,
  Keyboard,
  Question,
} from '@phosphor-icons/react';

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsHelp({
  isOpen,
  onClose,
}: KeyboardShortcutsHelpProps) {
  if (!isOpen) {
    return null;
  }

  const shortcuts = [
    {
      category: 'Workflow Navigation',
      items: [
        { keys: ['1'], description: 'Go to Start (Templates)' },
        { keys: ['2'], description: 'Go to Select Data' },
        { keys: ['3'], description: 'Go to Quality Check' },
        { keys: ['4'], description: 'Go to Analyze Data' },
        { keys: ['5'], description: 'Go to Visualize' },
        { keys: ['6'], description: 'Go to Export' },
      ],
    },
    {
      category: 'General Navigation',
      items: [
        {
          keys: ['←', '→'],
          description: 'Navigate between available workflow steps',
        },
        { keys: ['?'], description: 'Show/hide keyboard shortcuts help' },
        { keys: ['Tab'], description: 'Navigate between interactive elements' },
        { keys: ['Shift', 'Tab'], description: 'Navigate backwards' },
        { keys: ['Enter'], description: 'Activate focused element' },
        { keys: ['Escape'], description: 'Close dialogs or cancel actions' },
      ],
    },
    {
      category: 'Data Actions',
      items: [
        {
          keys: ['Space'],
          description: 'Select/deselect dataset (when focused)',
        },
        { keys: ['Ctrl', 'A'], description: 'Select all visible datasets' },
        { keys: ['Delete'], description: 'Remove selected datasets' },
      ],
    },
  ];

  return (
    <div className='fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4'>
      <Card className='w-full max-w-2xl max-h-[80vh] overflow-y-auto'>
        <CardContent className='p-6'>
          <div className='flex items-center justify-between mb-6'>
            <div className='flex items-center gap-2'>
              <Keyboard className='w-5 h-5' />
              <h2 className='text-xl font-semibold'>Keyboard Shortcuts</h2>
            </div>
            <Button variant='outline' size='sm' onClick={onClose}>
              <Question className='w-4 h-4 mr-1' />
              Close
            </Button>
          </div>

          <div className='space-y-6'>
            {shortcuts.map(category => (
              <div key={category.category}>
                <h3 className='text-sm font-medium text-muted-foreground mb-3'>
                  {category.category}
                </h3>
                <div className='space-y-2'>
                  {category.items.map((shortcut, index) => (
                    <div
                      key={index}
                      className='flex items-center justify-between py-2'
                    >
                      <span className='text-sm'>{shortcut.description}</span>
                      <div className='flex items-center gap-1'>
                        {shortcut.keys.map((key, keyIndex) => (
                          <div
                            key={keyIndex}
                            className='flex items-center gap-1'
                          >
                            <kbd className='px-2 py-1 text-xs font-mono bg-muted border rounded'>
                              {key}
                            </kbd>
                            {keyIndex < shortcut.keys.length - 1 && (
                              <span className='text-xs text-muted-foreground'>
                                +
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {category.category !==
                  shortcuts[shortcuts.length - 1].category && (
                  <Separator className='mt-4' />
                )}
              </div>
            ))}
          </div>

          <div className='mt-6 p-4 bg-muted/50 rounded-lg'>
            <div className='flex items-start gap-2'>
              <Command className='w-4 h-4 mt-0.5 text-muted-foreground' />
              <div className='text-xs text-muted-foreground'>
                <strong>Tip:</strong> Keyboard shortcuts only work when you're
                not typing in input fields. The workflow navigation shortcuts
                (1-6) will only activate available steps based on your current
                progress.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
